/* KSGshop chrome behaviour.
 *
 * Two jobs, both of which CSS cannot do on its own:
 *
 *   1. Publish the chrome's measured height. Anchors need it for
 *      scroll-padding, and Horizon's sticky product column reads
 *      --header-height, which is only ever set by sections/header.liquid -
 *      a section this theme does not render. Left unset, the product media
 *      column pins to y=0 and slides under the header on every PDP.
 *
 *   2. Turn the back control into a history back, so returning to a
 *      collection returns to the place in it the shopper left.
 *
 *   3. Fade the category rail at whichever edge still has links past it.
 *      Nothing in CSS can know whether an element is scrolled.
 *
 *   4. Condense the chrome once the reader has left the top of the page,
 *      down to the way home, the search field and the cart. CSS cannot read
 *      scroll position without scroll-driven animations, and the
 *      reduced-motion rule in ksg-motion.css kills every animation outright,
 *      which would take the header with it.
 *
 * If this file never loads, the header still pins at full height and still
 * works. The condensing is the only thing lost.
 */

/* --- 0. let the reader keep the scroll they just made ----------------- */

/* Coming back to a collection restores the place it was left, which is the
 * point of the back control below. But the restore is not one event. The
 * browser puts the page roughly where it was as soon as there is enough
 * document to do it, and then applies the saved offset again when the load
 * event fires. Scroll in between - which is exactly what someone does the
 * instant a familiar page appears - and the second application throws that
 * away.
 *
 * Measured on a phone-width collection: restored to 1600 while still
 * "loading", reader scrolls to 490, and at the same millisecond readyState
 * turns "complete" it is at 1490, the saved offset. Small, and infuriating,
 * because it lands after the page looks finished.
 *
 * The obvious fix does not work, and it is worth saying so here: setting
 * history.scrollRestoration to "manual" once the reader starts scrolling
 * does NOT cancel the pending restore. Measured side by side - with and
 * without - and both snapped back identically. By the time there is a
 * gesture to react to, the browser has already decided.
 *
 * So this does not try to prevent the last word. It has the last word
 * instead: remember where the reader put the page, and once the load event
 * has been and gone, put it back there.
 *
 * Only ever between a reader's first scroll gesture and a moment after
 * load. Outside that window nothing here runs, so ordinary scrolling, an
 * anchor jump, or a link into the middle of a page are all untouched.
 *
 * The gestures are the ones that mean scrolling, not merely touching. A
 * pointerdown fires when someone taps a product while the page is still
 * loading, and treating that as scroll intent would strand them wherever
 * the page happened to be - the same complaint from the other end. */
const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
  'Spacebar',
]);

if (document.readyState !== 'complete') {
  const gestures = ['wheel', 'touchmove', 'keydown'];
  let readerY = null;

  /* Stops tracking at "complete" on purpose: the browser's final restore
     lands in that same moment, and adopting it would be recording the very
     jump this exists to undo. */
  const note = () => {
    if (readerY !== null && document.readyState !== 'complete') readerY = window.scrollY;
  };

  const takeOver = (event) => {
    if (event.type === 'keydown' && !SCROLL_KEYS.has(event.key)) return;
    if (document.readyState === 'complete') return;

    readerY = window.scrollY;
    addEventListener('scroll', note, { passive: true });
    for (const type of gestures) removeEventListener(type, takeOver, { capture: true });
  };

  for (const type of gestures) {
    addEventListener(type, takeOver, { passive: true, capture: true });
  }

  const putItBack = () => {
    if (readerY === null) return;
    /* behavior:'instant' for the reason given further down this file: both
       scrollports carry scroll-behavior:smooth, so a plain assignment
       animates, and a correction that animates is a second jump. */
    if (Math.abs(window.scrollY - readerY) > 4) {
      window.scrollTo({ top: readerY, behavior: 'instant' });
    }
  };

  const stopWatching = () => {
    readerY = null;
    removeEventListener('scroll', note);
  };

  addEventListener(
    'load',
    () => {
      /* Watched frame by frame rather than corrected once on a timer,
         because the restore can land just before this handler or a few
         frames after it, and a correction that arrives late is a second
         visible jump rather than a fix for the first.

         It corrects at most once and then stops. That is the whole reason
         it is safe to run at all: after load, readerY is frozen, so a loop
         that kept going would fight the reader's next real scroll. One
         discrepancy is the browser's last word; anything after it is the
         reader, and theirs to keep. */
      let frames = 20;

      const watch = () => {
        if (readerY === null) return;

        if (Math.abs(window.scrollY - readerY) > 4) {
          putItBack();
          stopWatching();
          return;
        }

        if (frames-- > 0) requestAnimationFrame(watch);
        else stopWatching();
      };

      watch();
      /* A belt for the braces: if frames never run - a backgrounded tab
         throttles rAF to nothing - the correction still happens. */
      setTimeout(() => {
        putItBack();
        stopWatching();
      }, 400);
    },
    { once: true }
  );
}

const group = document.getElementById('header-group');

if (group) {
  /* --- 1. measured height ------------------------------------------- */
  const publishHeight = () => {
    const h = group.offsetHeight;
    if (!h) return;
    const root = document.documentElement.style;
    root.setProperty('--ksg-header-h', h + 'px');
    root.setProperty('--header-height', h + 'px');
  };

  publishHeight();
  if ('ResizeObserver' in window) new ResizeObserver(publishHeight).observe(group);

  /* --- 2. the back control goes BACK -------------------------------- */

  /* It is an <a> pointing at the shelf the product belongs to, which is the
     right destination but the wrong journey: following it is a forward
     navigation to a fresh page, so a shopper who had scrolled halfway
     through a collection was returned to the top of it every time.

     The browser already restores the position on a real history
     back - measured: left at 2200, returned at 2200 - so the fix is to
     perform one rather than to rebuild scroll restoration.

     Only when there is somewhere of ours to go back to. Arriving from a
     search engine, a shared link or a new tab leaves no history, and there
     the href is exactly right: it goes to the shelf.

     The breadcrumb is deliberately left alone. It names a destination
     - "King's Stella" - and should go there. The arrow means back. Two
     controls, two meanings. */
  const back = group.querySelector('.ksg-header__back');
  if (back) {
    back.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      let cameFromUs = false;
      try {
        cameFromUs = !!document.referrer && new URL(document.referrer).origin === location.origin;
      } catch {
        cameFromUs = false;
      }
      if (!cameFromUs || history.length <= 1) return;
      event.preventDefault();
      history.back();
    });
  }

  /* --- 3. keep the category rail's fade honest ----------------------- */

  /* The CSS ships a right-hand fade, which is right at rest. This keeps it
     truthful as the rail moves: no fade on a side there is nothing more to
     see on, and none at all when every link already fits.

     The width is cached and only remeasured on resize, so the scroll
     handler reads scrollLeft and nothing else - no forced layout on a
     gesture the reader is in the middle of. */
  const rail = group.querySelector('.ksg-header__nav');
  if (rail) {
    let travel = 0;
    const FADE = 24;

    const edges = () => {
      const at = rail.scrollLeft;
      rail.style.setProperty('--ksg-rail-l', at > 4 ? FADE + 'px' : '0px');
      rail.style.setProperty('--ksg-rail-r', at < travel - 4 ? FADE + 'px' : '0px');
    };

    const remeasure = () => {
      travel = rail.scrollWidth - rail.clientWidth;
      edges();
    };

    remeasure();
    addEventListener('load', remeasure);
    addEventListener('resize', remeasure, { passive: true });
    rail.addEventListener('scroll', edges, { passive: true });
  }

  /* --- 4. condense once away from the top ---------------------------- */

  /* The merchant selects the header in the theme editor to edit it, and the
     editor scrolls to whatever is selected. A header that has condensed
     itself is a header they cannot watch themselves changing. */
  if (!document.documentElement.classList.contains('shopify-design-mode')) {
    /* Below 990px the document scrolls; at 990px and up base.css hands the
       scrollport to .page-wrapper. Rather than branch on the breakpoint and
       re-bind on resize, both are read and summed - only one of them is ever
       non-zero. */
    const wrapper = document.querySelector('.page-wrapper');
    const offset = () =>
      (wrapper ? wrapper.scrollTop : 0) + (window.scrollY || document.documentElement.scrollTop || 0);

    /* Both scrollports carry scroll-behavior:smooth from base.css - html
       below 990px, .page-wrapper above it - so an assignment to scrollTop
       ANIMATES. A correction that is meant to cancel a jump has to land in
       the same frame as the jump; animated, it becomes a visible slide of
       its own. behavior:'instant' on both paths is what makes it invisible.
       Reading scrollTop straight back after assigning it under smooth
       scrolling also returns the old value, which is what hid this. */
    const nudge = (px) => {
      if (!px) return;
      const target = wrapper && wrapper.scrollTop > 0 ? wrapper : window;
      const from = target === window ? window.scrollY : wrapper.scrollTop;
      target.scrollTo({ top: Math.max(0, from + px), behavior: 'instant' });
    };

    let slim = false;
    let queued = false;

    const update = () => {
      queued = false;

      /* Never change state while the reader is inside the header. On a
         phone the two search fields are two different elements, so
         condensing mid-typing would swap the focused one out and take
         whatever had been typed with it - and scrolling with the keyboard
         open is exactly when that happens. It also stops the header
         reshaping itself under a keyboard user tabbing through it. */
      if (group.contains(document.activeElement)) return;

      const now = offset();

      if (!slim) {
        /* Condense once the reader is past the header's own full height. The
           correction below is always smaller than that, so it can never land
           back inside the expand threshold - which is what would otherwise
           leave the header flicking between the two states forever. */
        if (now <= group.offsetHeight + 24) return;

        /* Condensing can pull the page up under the reader, and how much -
           or whether at all - depends on things this code should not be
           guessing at:

             in flow, no scroll anchoring (Safari)  the page moves by the
                                                    full height that went
             in flow, with anchoring (Chrome, FF)   the browser has already
                                                    put it back
             out of flow (the phone product page,   nothing moves at all
              where the chrome is fixed)

           Inferring it from the header's own height got the third case
           backwards: the bar GROWS from 36px to 56px there, so a
           height-based correction scrolled the page 20px further than the
           reader asked, at the exact moment they started reading.

           So measure the symptom rather than the cause. A probe in the page
           content says whether anything actually moved, and only what is
           left over gets corrected. All three cases fall out of one line.

           The probe has to be a laid-out element. #header-group is followed
           in the layout by a <script>, which has no box and would have
           reported 0 either side - a correction that silently never fired,
           and Safari left with the jump this exists to stop. */
        const probe = document.getElementById('MainContent');
        const was = probe ? probe.getBoundingClientRect().top : null;
        group.setAttribute('data-ksg-slim', '');
        slim = true;
        if (was !== null) nudge(probe.getBoundingClientRect().top - was);
      } else if (now <= 4) {
        /* Expanding needs no correction. It only happens at the top of the
           page, where there is nothing above the reader to hold still and
           the header coming back is the whole point. */
        group.removeAttribute('data-ksg-slim');
        slim = false;
      }
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };

    addEventListener('scroll', onScroll, { passive: true });
    if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });
  }
}
